import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { Send, Zap, Paperclip, Mic, ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, Plus, Mail, Pause, Play, Search } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { OUTREACH_DATA, OUTREACH_CHAT_RESPONSES } from '../../data/outreachData';
import V4AdHocChat from './V4AdHocChat';
import { DAVID_SEQUENCE_DD, DAVID_OUTREACH_OVERALL_DD, DAVID_OUTREACH_CREATE, DAVID_OUTREACH_KEYPRESS } from '../../data/davidOutreachDDData';

const SequenceCard = ({ seq, isDark, onDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const isNew = seq.isNew;
  const cardBg = isNew
    ? (isDark ? 'bg-indigo-950/20' : 'bg-indigo-50/60')
    : seq.alert
      ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40')
      : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = isNew
    ? (isDark ? 'border-indigo-500/25' : 'border-indigo-300/60')
    : seq.alert
      ? (isDark ? 'border-red-500/15' : 'border-red-200/50')
      : border;
  const statusColor = seq.status === 'active' ? (isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-50') : (isDark ? 'text-amber-400 bg-amber-400/10' : 'text-amber-600 bg-amber-50');
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl p-3`} data-testid={`sequence-${seq.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Mail className={`w-3.5 h-3.5 ${seq.alert ? (isDark ? 'text-red-400' : 'text-red-500') : (isDark ? 'text-indigo-400' : 'text-indigo-600')}`} />
        <p className={`text-[11px] font-semibold flex-1 min-w-0 truncate ${tp}`}>{seq.name}</p>
        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 ${statusColor}`}>
          {seq.status === 'active' ? <Play className="w-2 h-2" /> : <Pause className="w-2 h-2" />}
          {seq.status}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        <div><p className={`text-[9px] ${ts}`}>Enrolled</p><p className={`text-sm font-bold ${tp}`}>{seq.enrolled}</p></div>
        <div><p className={`text-[9px] ${ts}`}>Replied</p><p className={`text-sm font-bold ${tp}`}>{seq.replied}</p></div>
        <div><p className={`text-[9px] ${ts}`}>Reply Rate</p><p className={`text-sm font-bold ${seq.alert ? (isDark ? 'text-red-400' : 'text-red-500') : tp}`}>{seq.replyRate}</p></div>
        <div><p className={`text-[9px] ${ts}`}>Meetings</p><p className={`text-sm font-bold ${tp}`}>{seq.meetings}</p></div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[9px] ${ts}`}>{seq.steps} steps</span>
        <span className={`text-[9px] font-medium ${parseFloat(seq.meetingRate) >= 15 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : parseFloat(seq.meetingRate) < 10 ? (isDark ? 'text-amber-400' : 'text-amber-600') : tp}`}>{seq.meetingRate} meeting rate</span>
        {onDeepDive && (
          <button
            onClick={() => onDeepDive(seq.id)}
            className={`ml-auto flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
            data-testid={`sequence-deepdive-${seq.id}`}
          >
            <Zap className="w-3 h-3" /> Deep Dive
          </button>
        )}
      </div>
    </div>
  );
};

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const RepRow = ({ rep, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <tr className={`border-b ${border}`} data-testid={`rep-${rep.name.replace(/\s+/g, '-').toLowerCase()}`}>
      <td className={`py-2 px-3 text-[11px] font-medium ${tp}`}>{rep.name}</td>
      <td className={`py-2 px-2 text-[11px] text-right ${ts}`}>{rep.sent}</td>
      <td className={`py-2 px-2 text-[11px] text-right ${tp}`}>{rep.replied}</td>
      <td className={`py-2 px-2 text-[11px] text-right font-medium ${tp}`}>{rep.replyRate}</td>
      <td className={`py-2 px-2 text-[11px] text-right font-medium ${tp}`}>{rep.meetings}</td>
    </tr>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Sally · {item.time}</p>
      </div>
    </div>
  );
};

const V4SalesOutreach = ({ isDark, onWorkflowToggle, workflowPanelOpen, activeUser, davidState, outreachAdHocScenario, onOutreachAdHocDone }) => {
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [sequenceDDId, setSequenceDDId] = useState(null);
  const [overviewDD, setOverviewDD] = useState(false);
  const [newSeqFlow, setNewSeqFlow] = useState(false);
  const messagesEndRef = useRef(null);

  const isDavid = activeUser?.id === 'sales';

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || OUTREACH_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCreateSequence = () => {
    if (isDavid) { setNewSeqFlow(true); return; }
    sendMessage('I want to build a new outreach sequence.', OUTREACH_CHAT_RESPONSES.create);
  };

  const handleBackFromNewSeq = () => {
    const s = DAVID_OUTREACH_CREATE['new-sequence'];
    if (s?.createdSequence && davidState?.createDavidSequence) {
      davidState.createDavidSequence(s.createdSequence);
    }
    setNewSeqFlow(false);
  };

  const allSequences = useMemo(() => {
    const created = davidState?.createdSequences || [];
    return [...created, ...OUTREACH_DATA.sequences];
  }, [davidState?.createdSequences]);

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Sally about outreach..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto' }} data-testid="outreach-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.sally} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Sally</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="outreach-send-btn">
              <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Overall Outreach DD (top-right, David-only) ── */
  if (overviewDD && isDavid) {
    const s = DAVID_OUTREACH_OVERALL_DD.overview;
    return <V4AdHocChat isDark={isDark} scenarioId={s.id} scenarios={{ [s.id]: s }} backLabel="Back to Outreach" onBack={() => setOverviewDD(false)} />;
  }

  /* ── Per-sequence DD (David-only) ── */
  if (sequenceDDId && isDavid) {
    const s = DAVID_SEQUENCE_DD[sequenceDDId];
    if (s) {
      return <V4AdHocChat isDark={isDark} scenarioId={s.id} scenarios={{ [s.id]: s }} backLabel="Back to Outreach" onBack={() => setSequenceDDId(null)} />;
    }
  }

  /* ── New Sequence flow (David-only) ── */
  if (newSeqFlow && isDavid) {
    return <V4AdHocChat isDark={isDark} scenarioId="new-sequence" scenarios={DAVID_OUTREACH_CREATE} backLabel="Back to Outreach" onBack={handleBackFromNewSeq} />;
  }

  /* ── Keypress A (David-only) ── */
  if (outreachAdHocScenario && isDavid) {
    return <V4AdHocChat isDark={isDark} scenarioId={outreachAdHocScenario} scenarios={DAVID_OUTREACH_KEYPRESS} backLabel="Back to Outreach" onBack={() => onOutreachAdHocDone?.()} skipFirstUserDelay />;
  }

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="outreach-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`}><ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Outreach</span></button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.sally} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const d = OUTREACH_DATA;
  return (
    <div className="flex flex-col h-full" data-testid="outreach-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Mail className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Outreach</p>
            <span className={`text-[10px] ${ts}`}>{allSequences.filter(s => s.status === 'active').length} active sequences</span>
          </div>
          <div className="flex items-center gap-2">
            {isDavid && (
              <button
                onClick={() => setOverviewDD(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
                data-testid="outreach-deep-dive-btn"
              >
                <Search className="w-3.5 h-3.5" /> Deep Dive
              </button>
            )}
            <button onClick={handleCreateSequence} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="outreach-create-btn">
              <Plus className="w-3.5 h-3.5" /> New Sequence
            </button>
          </div>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        {/* Metrics */}
        <div className="grid grid-cols-6 gap-3">
          {d.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Sequences */}
        <div>
          <p className={`text-xs font-semibold mb-3 ${tp}`}>Active Sequences</p>
          <div className="grid grid-cols-2 gap-3">
            {allSequences.map(seq => <SequenceCard key={seq.id} seq={seq} isDark={isDark} onDeepDive={isDavid ? setSequenceDDId : null} />)}
          </div>
        </div>

        {/* Rep leaderboard + Insights */}
        <div className="grid grid-cols-2 gap-5">
          <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl p-4`}>
            <p className={`text-[11px] font-semibold mb-3 ${tp}`}>Rep Performance</p>
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}>
                  <th className={`py-1.5 px-3 text-[9px] font-semibold text-left ${ts}`}>Rep</th>
                  <th className={`py-1.5 px-2 text-[9px] font-semibold text-right ${ts}`}>Sent</th>
                  <th className={`py-1.5 px-2 text-[9px] font-semibold text-right ${ts}`}>Replied</th>
                  <th className={`py-1.5 px-2 text-[9px] font-semibold text-right ${ts}`}>Rate</th>
                  <th className={`py-1.5 px-2 text-[9px] font-semibold text-right ${ts}`}>Meetings</th>
                </tr>
              </thead>
              <tbody>
                {d.topPerformers.map(rep => <RepRow key={rep.name} rep={rep} isDark={isDark} />)}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2 mb-0.5">
              <img src={AGENT_THUMBS.sally} alt="" className="w-4 h-4 rounded-full object-cover" />
              <p className={`text-[11px] font-semibold ${tp}`}>Sally's Insights</p>
            </div>
            {d.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SalesOutreach;
